import { Package, Upload, CheckCircle2, Loader2, FolderOpen, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Progress } from '@/components/ui/progress'
import { useCreateModel } from './context'

type Step = 'preparing' | 'uploading' | 'submitted'

const STEPS: { key: Step; label: string; description: string; icon: typeof Package }[] = [
  { key: 'preparing', label: 'Preparing dataset', description: 'Zipping images and captions...', icon: Package },
  { key: 'uploading', label: 'Uploading to training server', description: 'Sending your dataset to fal.ai...', icon: Upload },
  { key: 'submitted', label: 'Training started', description: 'Your model is now being trained on the cloud.', icon: CheckCircle2 },
]

function getStepIndex(status: string): number {
  return STEPS.findIndex((s) => s.key === status)
}

export function SubmitProgress() {
  const { state, estimatedCost, selectedTrainer } = useCreateModel()

  const currentIndex = getStepIndex(state.submitStatus)
  const isSubmitted = state.submitStatus === 'submitted'
  const uploadProgress = state.uploadProgress // Real progress from XHR upload

  return (
    <div className="flex flex-col items-center gap-6 px-6 py-10">
      {/* Model info */}
      <div className="text-center">
        <h3 className="text-lg font-semibold">{state.modelName || 'Untitled Model'}</h3>
        <p className="text-sm text-muted-foreground">
          {selectedTrainer?.name} &middot; {state.images.length} images
          {estimatedCost && ` · $${estimatedCost.totalCost.toFixed(2)}`}
        </p>
      </div>

      {/* Steps */}
      <div className="flex w-full max-w-md flex-col gap-3">
        {STEPS.map((step, index) => {
          const isActive = index === currentIndex
          const isCompleted = index < currentIndex
          const isPending = index > currentIndex
          const StepIcon = step.icon

          return (
            <div
              key={step.key}
              className={cn(
                'flex items-start gap-3 rounded-lg border px-4 py-3 transition-all',
                isActive && 'border-primary bg-primary/5',
                isCompleted && 'border-border',
                isPending && 'border-transparent opacity-40',
              )}
            >
              <div className={cn(
                'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                (isCompleted || isActive) && 'bg-primary/10 text-primary',
                isPending && 'bg-muted text-muted-foreground',
              )}>
                {isActive && step.key !== 'submitted' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : isCompleted ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : step.key === 'submitted' && isActive ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <StepIcon className="h-3.5 w-3.5" />
                )}
              </div>

              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className={cn('text-sm font-medium', isPending && 'text-muted-foreground')}>
                  {step.label}
                </p>
                <p className="text-xs text-muted-foreground">{step.description}</p>

                {step.key === 'uploading' && isActive && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <Progress value={uploadProgress} size="sm" className="flex-1" />
                    <span className="text-[11px] text-muted-foreground">{Math.round(uploadProgress)}%</span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Completed: summary + where to find */}
      {isSubmitted && (
        <div className="flex w-full max-w-md flex-col gap-3">
          {/* Duration + cost */}
          {estimatedCost && (
            <div className="rounded-lg border border-border px-4 py-3 text-center">
              <p className="text-xs text-muted-foreground">Estimated training time</p>
              <p className="text-lg font-semibold">{estimatedCost.estimatedDuration}</p>
              <p className="text-xs text-muted-foreground">
                ${estimatedCost.totalCost.toFixed(2)} deducted from wallet
              </p>
            </div>
          )}

          {/* Where to find */}
          <div className="rounded-lg border border-border px-4 py-3">
            <p className="mb-2 text-xs font-medium">Where to find your model</p>
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2.5">
                <FolderOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-xs font-medium">Files sidebar</p>
                  <p className="text-[11px] text-muted-foreground">
                    Your trained model will appear in My Models once training completes
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div>
                  <p className="text-xs font-medium">Canvas nodes</p>
                  <p className="text-[11px] text-muted-foreground">
                    Use it in any image generation node — select your LoRA from the Custom Models section
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
